/**
 * Home — Melodimals' TOY SCENE.
 *
 * There is no web menu: there is an illustrated full-bleed meadow where every
 * module is an OBJECT with personality that can be touched (Melodimals creatures
 * and instruments). Every touch responds with sound + bounce + haptics, and the
 * journey to the module is a burst of color from the object (see
 * docs/DESIGN_TOY.md).
 *
 * Access to the parents' area goes through the parental gate; the sticker
 * showcase is a touchable album (without a gate).
 */

import { createEffect, createSignal, For, onMount, untrack } from "solid-js";
import { createRoute, Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { initMonetization, maybeShowInterstitial } from "@melodimals/monetization";
import { rootRoute } from "@/routes/__root";
import { useI18n } from "@/providers/i18n-provider";
import { useAppConfig } from "@/providers/config-provider";
import { useSettings } from "@/providers/settings-provider";
import type { GAME_MODULES } from "@/games/registry";
import { availableGameModules } from "@/games/registry";
import { ParentalGate } from "@/features/shell/parental-gate";
import { markGatePassed } from "@/features/shell/gate-pass";
import { BackgroundMusic } from "@/features/shell/background-music";
import { Wordmark } from "@/components/brand/wordmark";
import { SceneBackdrop } from "@/features/home/scene-backdrop";
import { ToyObject } from "@/features/home/toy-object";
import { ModuleObjectArt, ParentsDoorArt, StickerAlbumArt } from "@/features/home/object-art";
import { createSceneLaunch } from "@/features/home/scene-transition";
import { playTapPop } from "@/lib/toy/ui-sfx";
import { vibrate } from "@/lib/gestures/haptics";

/** Organic tilts by position: the objects "live" in the meadow. */
const TILTS = [-3, 2.5, -2, 3, 2, -3, 2.5, -2];

/** Module accent (registry) → burst travel color. */
const BURST_COLORS: Record<string, string> = {
  "bg-joy-coral": "var(--joy-coral)",
  "bg-joy-tangerine": "var(--joy-tangerine)",
  "bg-joy-sunshine": "var(--joy-sunshine)",
  "bg-joy-mint": "var(--joy-mint)",
  "bg-joy-grass": "var(--joy-grass)",
  "bg-joy-sky": "var(--joy-sky)",
  "bg-joy-grape": "var(--joy-grape)",
  "bg-joy-bubblegum": "var(--joy-bubblegum)",
};

function accentToBurstColor(accentClass: string): string {
  return BURST_COLORS[accentClass] ?? "var(--joy-sunshine)";
}

function HomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const settings = useSettings();
  const { config } = useAppConfig();
  const search = useSearch({ from: "/" });
  const [gateOpen, setGateOpen] = createSignal(false);
  const { launch, BurstLayer } = createSceneLaunch();

  onMount(() => {
    void initMonetization();
  });

  // URL-addressability of the parental gate from home (#11): `?gate=1` opens the
  // gate on load (consistent deep link; the real flow was already covered by
  // /parents). Idempotent URL→signal effect; opening/closing it is reflected in the URL
  // with replace so the history does not get dirty. The default (closed) omits the param.
  const setGate = (open: boolean) => {
    setGateOpen(open);
    void navigate({ to: "/", search: open ? { gate: 1 } : {}, replace: true });
  };

  // The `!gateOpen()` check belongs in `untrack`: the effect should react only
  // to EXTERNAL changes to `?gate=`. If it tracked `gateOpen()` (the signal that
  // writes it), closing the gate state-first (setGate(false)) would retrigger the effect
  // with the STILL-old search (`gate=1`, navigate async) and immediately REOPEN it
  // → the parental gate could not be dismissed (bug #15b / CC-16).
  createEffect(() => {
    if (search().gate === 1 && untrack(() => !gateOpen())) {
      setGateOpen(true);
    }
  });

  /**
   * Tap on a module object: its color burst and journey. The interstitial
   * (the only allowed point, with a frequency cap inside maybeShowInterstitial)
   * occurs after the burst, just before navigating.
   */
  const openModule = (event: MouseEvent, module: (typeof GAME_MODULES)[number]) => {
    launch(event, accentToBurstColor(module.manifest.accentClass), () => {
      void (async () => {
        await maybeShowInterstitial();
        await navigate({ to: module.to, params: module.params });
      })();
    });
  };

  const openCollection = (event: MouseEvent) => {
    launch(event, "var(--joy-bubblegum)", () => {
      void navigate({ to: "/collection" });
    });
  };

  return (
    <main class="kid-screen relative items-center justify-center gap-[clamp(0.75rem,2.5vh,1.5rem)] bg-background p-[clamp(0.75rem,3vw,1.5rem)] select-none">
      <BackgroundMusic />
      <SceneBackdrop />

      {/* Cartel de marca colgado sobre la pradera. */}
      <div class="animate-pop-in relative">
        <Link
          to="/"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
        >
          <Wordmark />
        </Link>
      </div>

      {/* La pradera: cada módulo es un objeto ilustrado tocable. */}
      <div class="toy-meadow relative grid w-full max-w-3xl gap-[clamp(0.5rem,2vw,1.25rem)]">
        <For each={availableGameModules()}>
          {(module, i) => (
            <ToyObject
              label={module.manifest.id}
              tone={i()}
              tilt={TILTS[i() % TILTS.length]}
              appearDelayMs={i() * 55}
              onActivate={(event) => {
                openModule(event, module);
              }}
            >
              <ModuleObjectArt id={module.manifest.id} class="drop-shadow-md" />
            </ToyObject>
          )}
        </For>
      </div>

      <div class="relative flex items-center gap-[clamp(1rem,4vw,2rem)]">
        {/* Álbum de stickers: la vitrina de premios (sin puerta). */}
        <ToyObject
          label={t(msg`My stickers`)}
          tone={8}
          tilt={-4}
          appearDelayMs={460}
          class="h-20 w-20"
          onActivate={openCollection}
        >
          <StickerAlbumArt class="drop-shadow-md" />
        </ToyObject>

        {/* Puerta "de personas mayores": única superficie con texto del home
            (habla a adultos, no a pre-lectores; Families Policy). */}
        <button
          type="button"
          onClick={() => {
            playTapPop(settings.sfxVolume(), 5);
            vibrate(10);
            setGate(true);
          }}
          class="toy-parents-sign text-base"
        >
          {/* pop-in en el wrapper interno: el botón no escala su caja táctil */}
          <span
            class="animate-pop-in flex items-center gap-2"
            style={{ "animation-delay": "260ms" }}
          >
            <span class="h-10 w-10" aria-hidden="true">
              <ParentsDoorArt />
            </span>
            {t(msg`For parents`)}
          </span>
        </button>
      </div>

      <BurstLayer />

      <ParentalGate
        open={gateOpen()}
        onSuccess={() => {
          setGate(false);
          markGatePassed(config().parental.gatePassMinutes);
          void navigate({ to: "/parents" });
        }}
        onDismiss={() => setGate(false)}
      />
    </main>
  );
}

/** Addressable parental gate: `?gate=1` opens it on load. The default (closed)
 * = absence of the parameter, so we omit it to keep the home URL clean. */
interface HomeSearch {
  gate?: 1;
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>): HomeSearch => ({
    gate: search.gate === 1 || search.gate === "1" ? 1 : undefined,
  }),
  component: HomePage,
});
